import { useEffect, useState } from 'react';
import parameterSvc from '../services/api/parameter';
import parameterUnitSvc from '../services/api/parameterUnit';
import commonSvc from '../services/api/common';
import stationSvc from '../services/api/station';
import problemSvc from '../services/api/problem';
import rolesSvc from '../services/api/roles';

/** Cache cho dữ liệu lookup thông số. */
let lookupParameterCache: any[] | null = null;
/** Cache cho dữ liệu lookup đơn vị thông số. */
let lookupParameterUnitCache: any[] | null = null;
/** Cache cho dữ liệu lookup trạng thái thiết bị. */
let lookupDeviceStatusesCache: any[] | null = null;
/** Cache cho dữ liệu lookup trạm. */
let lookupStationCache : any[] | null = null;
/** Cache cho dữ liệu lookup trạng thái sự cố. */
let lookupProblemStatusCache : any[] | null = null;
/** Cache cho dữ liệu lookup vai trò. */
let lookupRoleCache : any[] | null = null;

/**
 * Custom hook để lấy danh sách các thông số (parameter).
 * Dữ liệu được cache lại sau lần gọi đầu tiên để tăng hiệu suất.
 * @returns {{lookupParameter: any[], loading: boolean, error: Error | null}} - Object chứa danh sách thông số, trạng thái loading và lỗi (nếu có).
 */
export const useLookupParameter = () => {
    const [lookupParameter, setLookupParameter] = useState<any[]>(lookupParameterCache || []);
    const [loading, setLoading] = useState(!lookupParameterCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupParameterCache) {
            setLoading(true);
            parameterSvc.getAll({ pageIndex: 1, pageSize: 100 })
                .then(res => {
                    if (res && res.content) {
                        lookupParameterCache = res.content.items.map((item: any) => ({ ...item, value: item.id, label: item.name }));
                        setLookupParameter(lookupParameterCache);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupParameter, loading, error };
};

/**
 * Custom hook để lấy danh sách các đơn vị của thông số (parameter unit).
 * Dữ liệu được cache lại sau lần gọi đầu tiên.
 * @returns {{lookupParameterUnit: any[], loading: boolean, error: Error | null}} - Object chứa danh sách đơn vị, trạng thái loading và lỗi.
 */
export const useLookupParameterUnit = () => {
    const [lookupParameterUnit, setLookupParameterUnit] = useState<any[]>(lookupParameterUnitCache || []);
    const [loading, setLoading] = useState(!lookupParameterUnitCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupParameterUnitCache) {
            setLoading(true);
            parameterUnitSvc.getLookup()
                .then(res => {
                    if (res && res.content) {
                        lookupParameterUnitCache = res.content;
                        setLookupParameterUnit(res.content);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupParameterUnit, loading, error };
};

/**
 * Custom hook để lấy danh sách các trạng thái của thiết bị.
 * Dữ liệu được cache lại sau lần gọi đầu tiên.
 * @returns {{lookupDeviceStatuses: any[], loading: boolean, error: Error | null}} - Object chứa danh sách trạng thái, trạng thái loading và lỗi.
 */
export const useLookupDeviceStatuses = () => {
    const [lookupDeviceStatuses, setLookupDeviceStatuses] = useState<any[]>(lookupDeviceStatusesCache || []);
    const [loading, setLoading] = useState(!lookupDeviceStatusesCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupDeviceStatusesCache) {
            setLoading(true);
            commonSvc.getLookupDeviceStatuses()
                .then(res => {
                    if (res && res.content) {
                        lookupDeviceStatusesCache = res.content;
                        setLookupDeviceStatuses(res.content);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupDeviceStatuses, loading, error };
};

/**
 * Custom hook để lấy danh sách các trạm (station).
 * Dữ liệu được cache lại sau lần gọi đầu tiên.
 * @returns {{lookupStation: any[], loading: boolean, error: Error | null}} - Object chứa danh sách trạm, trạng thái loading và lỗi.
 */
export const useLookupStation = () => {
    const [lookupStation, setLookupStation] = useState<any[]>(lookupStationCache || []);
    const [loading, setLoading] = useState(!lookupStationCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupStationCache) {
            setLoading(true);
            stationSvc.getLookup()
                .then(res => {
                    if (res && res.content) {
                        lookupStationCache = res.content;
                        setLookupStation(res.content);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupStation, loading, error };
};

/**
 * Custom hook để lấy danh sách các trạng thái sự cố (problem status).
 * Dữ liệu được cache lại sau lần gọi đầu tiên.
 * @returns {{lookupProblemStatus: any[], loading: boolean, error: Error | null}} - Object chứa danh sách trạng thái sự cố, trạng thái loading và lỗi.
 */
export const useLookupProblemStatus = () => {
    const [lookupProblemStatus, setLookupProblemStatus] = useState<any[]>(lookupProblemStatusCache || []);
    const [loading, setLoading] = useState(!lookupProblemStatusCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupProblemStatusCache) {
            setLoading(true);
            problemSvc.getLookupProblemStatus()
                .then(res => {
                    if (res && res.content) {
                        lookupProblemStatusCache = res.content;
                        setLookupProblemStatus(res.content);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupProblemStatus, loading, error };
};

/**
 * Custom hook để lấy danh sách các vai trò (role).
 * Dữ liệu được cache lại sau lần gọi đầu tiên.
 * @returns {{lookupRole: any[], loading: boolean, error: Error | null}} - Object chứa danh sách vai trò, trạng thái loading và lỗi.
 */
export const useLookupRole = () => {
    const [lookupRole, setLookupRole] = useState<any[]>(lookupRoleCache || []);
    const [loading, setLoading] = useState(!lookupRoleCache);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!lookupRoleCache) {
            setLoading(true);
            rolesSvc.getLookup()
                .then(res => {
                    if (res && res.content) {
                        lookupRoleCache = res.content.map((item: any) => ({ ...item, value: item.id, label: item.name }));
                        setLookupRole(lookupRoleCache);
                    }
                })
                .catch(e => setError(e))
                .finally(() => setLoading(false));
        }
    }, []);

    return { lookupRole, loading, error };
};
